import { useQuery } from "@tanstack/react-query";
import { Link } from "@/lib/router-compat";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowRight } from "lucide-react";
import { ProductRatingBadge } from "@/components/ProductRatingBadge";

interface RelatedArticlesProps {
  currentArticleId: string;
  category: string | null;
  tags: string[] | null;
  /** Server-fetched list from the route loader so the block is in the initial HTML. */
  initialArticles?: RelatedArticle[];
}

export interface RelatedArticle {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  featured_image: string | null;
  category: string | null;
  tags: string[] | null;
  product_rating?: number | null;
}

type Article = RelatedArticle;

/** 4–6 published articles sharing tags, then category, scored by overlap. */
export async function fetchRelatedArticles(
  currentArticleId: string,
  category: string | null,
  tags: string[] | null,
): Promise<RelatedArticle[]> {
      // First, try to find articles with matching tags
      let articles: Article[] = [];
      
      if (tags && tags.length > 0) {
        const { data: tagMatches } = await supabase
          .from('articles')
          .select('id, title, slug, excerpt, featured_image, category, tags, product_rating')
          .eq('status', 'published')
          .neq('id', currentArticleId)
          .overlaps('tags', tags)
          .limit(12);
        
        if (tagMatches) {
          articles = tagMatches;
        }
      }
      
      // If we don't have enough, get articles from the same category
      if (articles.length < 6 && category) {
        const existingIds = articles.map(a => a.id);
        const { data: categoryMatches } = await supabase
          .from('articles')
          .select('id, title, slug, excerpt, featured_image, category, tags, product_rating')
          .eq('status', 'published')
          .eq('category', category)
          .neq('id', currentArticleId)
          .not('id', 'in', `(${existingIds.length > 0 ? existingIds.join(',') : 'null'})`)
          .limit(12 - articles.length);
        
        if (categoryMatches) {
          articles = [...articles, ...categoryMatches];
        }
      }
      
      // Score and sort by relevance (number of matching tags)
      const scored = articles.map(article => {
        let score = 0;
        if (tags && article.tags) {
          score = tags.filter(tag => article.tags?.includes(tag)).length * 2;
        }
        if (category && article.category === category) {
          score += 1;
        }
        return { ...article, score };
      });
      
      return scored
        .sort((a, b) => b.score - a.score)
        .slice(0, 6)
        .map(({ score: _score, ...a }) => a);
}

const RelatedArticles = ({ currentArticleId, category, tags, initialArticles }: RelatedArticlesProps) => {
  const { data: relatedArticles, isLoading } = useQuery({
    queryKey: ['related-articles', currentArticleId, category, tags],
    queryFn: () => fetchRelatedArticles(currentArticleId, category, tags),
    enabled: !!(category || (tags && tags.length > 0)),
    ...(initialArticles ? { initialData: initialArticles } : {}),
  });

  if (isLoading) {
    return (
      <section className="mt-16 pt-8 border-t border-border">
        <h2 className="text-2xl font-bold text-foreground mb-6">Related Articles</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="overflow-hidden">
              <Skeleton className="h-40 w-full" />
              <CardContent className="p-4">
                <Skeleton className="h-4 w-20 mb-2" />
                <Skeleton className="h-6 w-full mb-2" />
                <Skeleton className="h-4 w-3/4" />
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
    );
  }

  if (!relatedArticles || relatedArticles.length === 0) {
    return null;
  }

  return (
    <section className="mt-16 pt-8 border-t border-border">
      <h2 className="text-2xl font-bold text-foreground mb-6">Related Articles</h2>
      <div className="grid md:grid-cols-3 gap-6">
        {relatedArticles.map((article) => (
          <Link 
            key={article.id}
            to={`/articles/${article.slug}`}
            className="group"
          >
            <Card className="overflow-hidden h-full transition-all duration-300 hover:shadow-lg hover:border-primary/50">
              {article.featured_image && (
                <div className="aspect-video overflow-hidden relative">
                  <img
                    src={article.featured_image}
                    alt={article.title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
              )}
              <CardContent className="p-4">
                {article.category && (
                  <Badge variant="secondary" className="mb-2 text-xs">
                    {article.category}
                  </Badge>
                )}
                <h3 className="font-semibold text-foreground mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                  {article.title}
                </h3>
                {article.excerpt && (
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                    {article.excerpt}
                  </p>
                )}
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center text-sm text-primary font-medium">
                    Read the full article
                    <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                  <ProductRatingBadge rating={article.product_rating} />
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </section>
  );
};

export default RelatedArticles;
